import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageTour from "@/components/PageTour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck, Loader2, Plus, Copy, RefreshCw, Trash2, Mail, MessageCircle,
  KeyRound, Link2, Search, X, UserPlus, Clock, Inbox, Building2, Send, AlertTriangle, MapPin, Smartphone,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CredBox, copyText, portalOrigin as origin } from "@/components/PortalCredBox";

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return (iso || "").replace("T", " ").slice(0, 16); }
};

export default function PortaliCondomini() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [creds, setCreds] = useState(null); // credenziali generate (create/regenerate)
  const [sending, setSending] = useState(""); // canale in invio per la CredBox
  const [filterBuilding, setFilterBuilding] = useState("all"); // filtro condominio
  const [requests, setRequests] = useState([]); // richieste credenziali in attesa
  const [anagrafica, setAnagrafica] = useState([]); // condòmini in anagrafica (per abbinamento)
  const [assignSel, setAssignSel] = useState({}); // { [reqId]: resident }

  const sendCreds = async (c, channel) => {
    if (!c?.id || !c?.password) { toast.error("Rigenera prima la password"); return; }
    setSending(channel);
    try {
      const r = await api.post(`/condomino-portals/${c.id}/send-credentials`, { channel, password: c.password, origin_url: origin() });
      toast.success(channel === "email" ? `Credenziali inviate via email a ${r.data.to}` : `Credenziali inviate via SMS a ${r.data.to}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(""); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/condomino-portals"); setPortals(r.data || []); }
    catch (err) { console.error("Caricamento portali fallito:", err); toast.error("Impossibile caricare i portali"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Richieste di credenziali in attesa (dal link pubblico nelle email documenti)
  const loadRequests = useCallback(async () => {
    try { const r = await api.get("/credential-requests"); setRequests(r.data || []); }
    catch { /* silenzioso */ }
  }, []);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Anagrafica condòmini (per abbinare una richiesta al condominio corretto)
  useEffect(() => {
    if (!requests.length) return;
    api.get("/condomino-portals/residents")
      .then((r) => setAnagrafica(r.data || []))
      .catch(() => { /* silenzioso */ });
  }, [requests.length]);

  const acceptRequest = async (req) => {
    setBusyId(req.id);
    try {
      const resident = assignSel[req.id];
      const r = await api.post(`/credential-requests/${req.id}/accept`,
        resident ? { resident_id: resident.resident_id } : {});
      const p = r.data.portal || {};
      if (p.password) setCreds({ id: p.id, contact: p.contact, username: p.contact, password: p.password, email: p.contact, nome: p.contact_name || p.nome, building_name: (p.building_names && p.building_names[0]) || p.building_name });
      toast.success(p.already_existed ? "Portale già esistente: password rigenerata" : "Richiesta accettata: portale creato");
      setAssignSel((prev) => { const n = { ...prev }; delete n[req.id]; return n; });
      loadRequests(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusyId(""); }
  };
  const rejectRequest = async (req) => {
    setBusyId(req.id);
    try { await api.post(`/credential-requests/${req.id}/reject`); toast.success("Richiesta rifiutata"); loadRequests(); }
    catch { toast.error("Operazione non riuscita"); }
    finally { setBusyId(""); }
  };

  // --- Tour guidato: crea un portale DEMO e lo elimina a fine tour ---
  const startTourDemo = useCallback(async () => {
    try { await api.post("/tour-demo/portale"); await load(); }
    catch (e) { console.debug("tour demo portale:", e); }
  }, [load]);
  const endTourDemo = useCallback(async () => {
    try { await api.delete("/tour-demo/portale"); } catch (e) { console.debug("tour demo del:", e); }
    load();
  }, [load]);

  const PORTALI_TOUR_STEPS = [
    { title: "Portali Condòmini 🔐", body: "Ogni condòmino può avere un accesso personale al suo portale. Ho creato un portale di esempio per la guida — lo elimino alla fine. Esci con la X, riapri col pulsante \"Guida\"." },
    { selector: "create-portal-btn", title: "Crea un portale", body: "Genera l'accesso per un condòmino scelto dall'anagrafica. Servono almeno un'email o un numero di telefono." },
    { selector: "cred-requests-box", title: "Richieste in attesa", body: "Quando un condòmino chiede l'accesso dal link pubblico (presente nelle email dei documenti), la richiesta compare qui: puoi abbinarla al nominativo giusto in anagrafica e accettarla con un clic, creando subito il portale." },
    { selector: "portal-building-filter", title: "Filtra per condominio", body: "Con molti portali, filtra l'elenco per condominio per trovare subito quello che ti serve." },
    { selector: "portal-row-demo", title: "Un portale attivo", body: "Ogni riga mostra il condòmino, se ha già la password, l'ultimo accesso e se le credenziali sono già state inviate. Dal portale il condòmino vede pratiche, chat AI, il suo immobile e i numeri utili." },
    { selector: "portal-send-email-demo", title: "Invia le credenziali", body: "Con un clic rigeneri la password e la invii via email o SMS. Puoi anche copiare il link d'accesso o inviare un link di reset password." },
    { selector: "autofill-cf-btn", title: "Codici fiscali", body: "Precompila automaticamente i codici fiscali dall'anagrafica: servono per l'accesso con SPID/CIE e per abbinare l'immobile del condòmino." },
    { title: "Pronto! 🎉", body: "Elimino il portale di esempio. Riavvia questa guida quando vuoi dal pulsante \"Guida\" in alto." },
  ];

  const [cfEdit, setCfEdit] = useState("");
  const [cfVal, setCfVal] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const autofillCf = async () => {
    setAutofilling(true);
    try {
      const r = await api.post("/condomino-portals/autofill-cf");
      toast.success(r.data.filled > 0 ? `Compilati ${r.data.filled} codici fiscali dall'anagrafica` : "Nessun codice fiscale trovato in anagrafica");
      load();
    } catch (err) { console.error(err); toast.error("Errore precompilazione"); }
    finally { setAutofilling(false); }
  };
  const saveCf = async (p) => {
    try {
      await api.post(`/condomino-portals/${p.id}/codice-fiscale`, { codice_fiscale: cfVal });
      toast.success("Codice fiscale salvato");
      setCfEdit(""); setCfVal(""); load();
    } catch (err) { console.error(err); toast.error("Errore salvataggio C.F."); }
  };

  const regenerate = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/condomino-portals/${p.id}/regenerate`);
      setCreds({ id: p.id, contact: p.contact, username: r.data.username, password: r.data.password, email: p.email, telefono: p.telefono, nome: p.contact_name || p.nome, building_name: (p.building_names && p.building_names[0]) || p.building_name, sms_enabled: !!p.sms_enabled });
      toast.success("Nuova password generata");
      load();
    } catch (err) { console.error(err); toast.error("Errore rigenerazione"); }
    finally { setBusyId(""); }
  };

  const regenerateAndSend = async (p, channel) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/condomino-portals/${p.id}/regenerate`);
      const c = { id: p.id, username: r.data.username, password: r.data.password, email: p.email, telefono: p.telefono, nome: p.contact_name || p.nome, building_name: (p.building_names && p.building_names[0]) || p.building_name, sms_enabled: !!p.sms_enabled };
      setCreds({ ...c, contact: p.contact });
      const s = await api.post(`/condomino-portals/${p.id}/send-credentials`, { channel, password: c.password, origin_url: origin() });
      toast.success(channel === "email" ? `Credenziali inviate via email a ${s.data.to}` : `Credenziali inviate via SMS a ${s.data.to}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusyId(""); }
  };

  const sendResetLink = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/condomino-portals/${p.id}/send-reset`, { origin_url: origin() });
      toast.success(`Link di reset password inviato a ${r.data.to}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Invio link di reset non riuscito"); }
    finally { setBusyId(""); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    const key = toDelete.id || toDelete.contact;
    setBusyId(toDelete.id);
    try {
      await api.delete(`/condomino-portals/${encodeURIComponent(key)}`);
      setPortals((prev) => prev.filter((x) => (x.id || x.contact) !== key));
      toast.success("Portale eliminato");
      setToDelete(null);
    } catch (err) { console.error(err); toast.error("Errore eliminazione"); }
    finally { setBusyId(""); }
  };

  const labelsFor = (p) => {
    if (p.building_names && p.building_names.length) return p.building_names;
    if (p.building_missing) return ["Condominio eliminato"];
    return ["Senza condominio"];
  };
  const sortLabels = (a, b) => {
    const rank = (x) => (x === "Senza condominio" ? 2 : x === "Condominio eliminato" ? 1 : 0);
    return rank(a) - rank(b) || a.localeCompare(b);
  };
  const buildingOptions = Array.from(new Set((portals || []).flatMap(labelsFor))).sort(sortLabels);
  const visiblePortals = filterBuilding === "all" ? portals : (portals || []).filter((p) => labelsFor(p).includes(filterBuilding));

  return (
    <div className="h-screen overflow-y-auto" data-testid="portali-page">
      <div className="max-w-4xl mx-auto p-5 sm:p-8">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-head text-2xl font-extrabold tracking-tight flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-[#007BFF]" />Portali Condòmini
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Crea e gestisci gli accessi personali dei condòmini. Ogni portale mostra tutte le pratiche del contatto e la chat AI del suo condominio.</p>
            <PageTour storageKey="tour_portali" className="mt-2" steps={PORTALI_TOUR_STEPS} onOpen={startTourDemo} onClose={endTourDemo} />
          </div>
          <Button className="bg-[#007BFF] hover:bg-[#0056B3] shrink-0" data-testid="create-portal-btn" onClick={() => { setCreds(null); setShowCreate(true); }}>
            <Plus className="h-4 w-4 mr-1.5" />Crea portale
          </Button>
        </div>

        <div className="flex items-center justify-between gap-3 -mt-3 mb-4 flex-wrap">
          {buildingOptions.length > 0 ? (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Select value={filterBuilding} onValueChange={setFilterBuilding}>
                <SelectTrigger className="h-9 w-[240px]" data-testid="portal-building-filter">
                  <SelectValue placeholder="Filtra per condominio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="portal-filter-all">Tutti i condomìni</SelectItem>
                  {buildingOptions.map((b) => (
                    <SelectItem key={b} value={b} data-testid={`portal-filter-${b}`}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : <span />}
          <button data-testid="autofill-cf-btn" onClick={autofillCf} disabled={autofilling}
            className="text-xs flex items-center gap-1.5 text-[#007BFF] hover:underline disabled:opacity-50">
            {autofilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Precompila codici fiscali dall'anagrafica
          </button>
        </div>

        {creds && !showCreate && (
          <div className="mb-5"><CredBox creds={creds} smsEnabled={creds.sms_enabled !== false} onSend={(ch) => sendCreds(creds, ch)} sending={sending} /><div className="mt-2 text-right"><button onClick={() => setCreds(null)} className="text-xs text-muted-foreground hover:text-foreground">Nascondi credenziali</button></div></div>
        )}

        {requests.length > 0 && (
          <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-4" data-testid="cred-requests-box">
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="h-4 w-4 text-amber-600" />
              <h2 className="font-semibold text-sm">Richieste di credenziali in attesa <span className="text-red-600" data-testid="cred-requests-count">({requests.length})</span></h2>
            </div>
            <div className="space-y-2">
              {requests.map((req) => {
                const sel = assignSel[req.id];
                return (
                <div key={req.id} data-testid={`cred-request-${req.id}`} className="bg-card border border-border rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-3 flex-wrap">
                    <UserPlus className="h-4 w-4 text-[#007BFF] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{req.contact_name || `${req.nome} ${req.cognome}`.trim()}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{req.email} · {req.condominio || req.building_name || "condominio non indicato"}</div>
                      {req.indirizzo && <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1" data-testid={`cred-req-indirizzo-${req.id}`}><MapPin className="h-3 w-3 shrink-0" />{req.indirizzo}</div>}
                      <div className="text-[10px] text-muted-foreground/70">Richiesta il {fmtDate(req.created_at)}</div>
                    </div>
                    <button data-testid={`cred-accept-${req.id}`} onClick={() => acceptRequest(req)} disabled={busyId === req.id}
                      className="inline-flex items-center gap-1.5 bg-[#007BFF] hover:bg-[#0056B3] text-white text-xs font-semibold rounded-lg px-3 py-2 disabled:opacity-50">
                      {busyId === req.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                      {sel ? "Accetta e collega" : "Accetta e crea portale"}
                    </button>
                    <button data-testid={`cred-reject-${req.id}`} onClick={() => rejectRequest(req)} disabled={busyId === req.id}
                      className="inline-flex items-center gap-1.5 border border-border hover:bg-secondary text-xs font-medium rounded-lg px-3 py-2 disabled:opacity-50">
                      <X className="h-3.5 w-3.5" />Rifiuta
                    </button>
                  </div>
                  <ResidentAssign req={req} residents={anagrafica} selected={sel}
                    onSelect={(r) => setAssignSel((prev) => ({ ...prev, [req.id]: r }))}
                    onClear={() => setAssignSel((prev) => { const n = { ...prev }; delete n[req.id]; return n; })} />
                </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : portals.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="portali-empty">
            <ShieldCheck className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessun portale ancora creato.</p>
            <p className="text-xs mt-1">Crea il primo portale per dare a un condòmino l'accesso alle sue pratiche.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries((visiblePortals || []).reduce((acc, p) => {
              const labels = filterBuilding === "all" ? labelsFor(p) : labelsFor(p).filter((l) => l === filterBuilding);
              labels.forEach((k) => { (acc[k] = acc[k] || []).push(p); });
              return acc;
            }, {}))
              .sort((a, b) => sortLabels(a[0], b[0]))
              .map(([building, rows]) => {
              const groupMissing = building === "Condominio eliminato";
              return (
              <div key={building} data-testid={`portal-group-${building}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  {groupMissing
                    ? <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    : <Building2 className="h-4 w-4 text-[#007BFF]" />}
                  <h2 className={`text-sm font-bold tracking-tight ${groupMissing ? "text-red-600 dark:text-red-400" : ""}`}>{building}</h2>
                  {groupMissing && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">condominio eliminato</span>}
                  <span className="text-[11px] text-muted-foreground">· {rows.length} portal{rows.length === 1 ? "e" : "i"}</span>
                </div>
                <div className="space-y-2">
                  {rows.map((p) => (
              <div key={p.id} className={`rounded-lg p-4 flex items-center gap-4 border ${p.building_missing ? "bg-red-50 border-red-300 dark:bg-red-950/40 dark:border-red-900/70" : "bg-card border-border"}`} data-testid={p.is_demo ? "portal-row-demo" : `portal-row-${p.id}`}>
                <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${p.building_missing ? "bg-red-100 dark:bg-red-900/50" : "bg-secondary"}`}>
                  {p.channel === "whatsapp" ? <MessageCircle className="h-4.5 w-4.5 text-emerald-500" /> : <Mail className="h-4.5 w-4.5 text-[#007BFF]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold truncate">{p.contact_name || p.contact}</div>
                    {p.building_count > 1 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-[#007BFF] dark:bg-blue-900/50 dark:text-blue-300 shrink-0" data-testid={`portal-multi-${p.id}`} title={p.building_names?.join(", ")}>
                        <Building2 className="h-3 w-3" />in {p.building_count} condomìni
                      </span>
                    )}
                    {p.auto_created && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300 shrink-0" data-testid={`portal-sms-created-${p.id}`} title="Portale creato automaticamente al primo accesso del condòmino con il solo numero di cellulare">
                        <Smartphone className="h-3 w-3" />creato via SMS
                      </span>
                    )}
                    {p.building_missing && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 shrink-0" data-testid={`portal-missing-${p.id}`}>
                        <AlertTriangle className="h-3 w-3" />Condominio "{p.building_name || "eliminato"}" non più presente
                      </span>
                    )}
                    {p.unread_requests > 0 && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 shrink-0" data-testid={`portal-unread-${p.id}`}>
                        <Inbox className="h-3 w-3" />{p.unread_requests} da leggere
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{p.contact}</div>
                  {(p.login_identifiers && p.login_identifiers.length > 1) && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-1" data-testid={`portal-logins-${p.id}`}>
                      <span className="font-medium text-foreground/70">Accesso con:</span>
                      {p.login_identifiers.map((idf, k) => (
                        <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary border border-border">
                          {idf.includes("@") ? <Mail className="h-3 w-3" /> : <MessageCircle className="h-3 w-3" />}{idf}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                    <span>{p.pratiche_count} pratic{p.pratiche_count === 1 ? "a" : "he"}</span>
                    <span className={p.has_password ? "text-green-600" : "text-orange-500"}>{p.has_password ? "Password impostata" : "Senza password"}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {p.last_login_at ? `Ultimo accesso: ${fmtDate(p.last_login_at)}` : "Mai effettuato l'accesso"}
                    </span>
                    {p.creds_sent_at ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`portal-creds-sent-${p.id}`}>
                        <Send className="h-3 w-3" />
                        Credenziali inviate ({p.creds_sent_channel === "whatsapp" || p.creds_sent_channel === "sms" ? "SMS" : "email"}) il {fmtDate(p.creds_sent_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-orange-500" data-testid={`portal-creds-notsent-${p.id}`}>
                        <Send className="h-3 w-3" />Credenziali non ancora inviate
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[11px]" data-testid={`portal-cf-${p.id}`}>
                    {cfEdit === p.id ? (
                      <div className="flex items-center gap-1">
                        <input autoFocus value={cfVal} onChange={(e) => setCfVal(e.target.value)} placeholder="Codice fiscale" className="bg-secondary rounded px-2 py-1 text-[11px] font-mono uppercase outline-none w-44" />
                        <button onClick={() => saveCf(p)} className="text-[#007BFF] font-medium px-1">Salva</button>
                        <button onClick={() => { setCfEdit(""); setCfVal(""); }} className="text-muted-foreground px-1">Annulla</button>
                      </div>
                    ) : p.codice_fiscale ? (
                      <span className="text-muted-foreground">C.F. <span className="font-mono">{p.codice_fiscale}</span> · <button onClick={() => { setCfEdit(p.id); setCfVal(p.codice_fiscale); }} className="text-[#007BFF]">modifica</button></span>
                    ) : (
                      <button data-testid={`portal-set-cf-${p.id}`} onClick={() => { setCfEdit(p.id); setCfVal(""); }} className="text-amber-600 font-medium">+ Imposta codice fiscale</button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.email && (
                    <button title={p.building_missing ? "Invio bloccato: condominio eliminato" : "Rigenera e invia via email"} data-testid={p.is_demo ? "portal-send-email-demo" : `portal-send-email-${p.id}`} disabled={busyId === p.id || p.building_missing} onClick={() => regenerateAndSend(p, "email")} className="p-2 rounded-md hover:bg-secondary hover:text-[#007BFF] disabled:opacity-40 disabled:cursor-not-allowed"><Mail className="h-4 w-4" /></button>
                  )}
                  {p.telefono && (
                    <button title={p.building_missing ? "Invio bloccato: condominio eliminato" : (!p.sms_enabled ? "L'invio credenziali via SMS è incluso nei piani Premium ed Elite" : "Rigenera e invia via SMS")} data-testid={`portal-send-sms-${p.id}`} disabled={busyId === p.id || p.building_missing || !p.sms_enabled} onClick={() => regenerateAndSend(p, "sms")} className="p-2 rounded-md hover:bg-secondary hover:text-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"><MessageCircle className="h-4 w-4" /></button>
                  )}
                  <button title="Copia link" data-testid={`portal-copy-link-${p.id}`} onClick={() => copyText(`${origin()}${p.path}`, "Link copiato")} className="p-2 rounded-md hover:bg-secondary hover:text-[#007BFF]"><Link2 className="h-4 w-4" /></button>
                  <button title="Rigenera password" data-testid={`portal-regen-${p.id}`} disabled={busyId === p.id} onClick={() => regenerate(p)} className="p-2 rounded-md hover:bg-secondary hover:text-[#007BFF] disabled:opacity-50">
                    {busyId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </button>
                  {p.email && (
                    <button title={p.building_missing ? "Invio bloccato: condominio eliminato" : "Invia link di reset password (via email)"} data-testid={`portal-send-reset-${p.id}`} disabled={busyId === p.id || p.building_missing} onClick={() => sendResetLink(p)} className="p-2 rounded-md hover:bg-secondary hover:text-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"><KeyRound className="h-4 w-4" /></button>
                  )}
                  <button title="Elimina portale" data-testid={`portal-delete-${p.id}`} onClick={() => setToDelete(p)} className="p-2 rounded-md hover:bg-secondary hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <CreatePortalDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={(c) => { setCreds(c); load(); }} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-portal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il portale?</AlertDialogTitle>
            <AlertDialogDescription>
              L'accesso di <b>{toDelete?.contact}</b> verrà revocato immediatamente. Le pratiche non vengono eliminate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-md">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-portal" onClick={(e) => { e.preventDefault(); doDelete(); }} className="rounded-md bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const CreatePortalDialog = ({ open, onClose, onCreated }) => {
  const [residents, setResidents] = useState([]);
  const [loadingR, setLoadingR] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState("");

  useEffect(() => {
    if (!open) { setResult(null); setSearch(""); return; }
    setLoadingR(true);
    api.get("/condomino-portals/residents")
      .then((r) => setResidents(r.data || []))
      .catch((err) => console.error("Caricamento anagrafica fallito:", err))
      .finally(() => setLoadingR(false));
  }, [open]);

  const create = async (resident_id) => {
    setBusy(true);
    try {
      const r = await api.post("/condomino-portals/from-resident", { resident_id });
      const c = { id: r.data.id, contact: r.data.contact, username: r.data.username,
                  password: r.data.password, email: r.data.email, telefono: r.data.telefono };
      setResult(c);
      onCreated(c);
      toast.success("Portale creato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione portale"); }
    finally { setBusy(false); }
  };

  const sendFromDialog = async (channel) => {
    if (!result?.id) return;
    setSending(channel);
    try {
      const s = await api.post(`/condomino-portals/${result.id}/send-credentials`, { channel, password: result.password, origin_url: origin() });
      toast.success(channel === "email" ? `Inviate via email a ${s.data.to}` : `Inviate via SMS a ${s.data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
    finally { setSending(""); }
  };

  const filtered = residents.filter((r) =>
    (r.nome + " " + r.email + " " + r.telefono + " " + (r.building_name || "")).toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-portal-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#007BFF]" />Crea portale condòmino</DialogTitle>
          <DialogDescription>Il portale è disponibile solo per i condòmini inseriti in anagrafica (con email o telefono).</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <CredBox creds={result} smsEnabled={result.sms_enabled !== false} onSend={sendFromDialog} sending={sending} />
            <div className="flex justify-end"><Button variant="outline" onClick={onClose} data-testid="close-after-create">Chiudi</Button></div>
          </div>
        ) : (
          <>
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="resident-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca condòmino (nome, email, condominio)…" className="pl-8 h-9" />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-1 border border-border rounded-md p-1">
              {loadingR ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-xs text-muted-foreground py-8">Nessun condòmino in anagrafica con email o telefono.<br />Aggiungi i residenti nella sezione Condomìni.</div>
              ) : filtered.map((r) => (
                <button key={r.resident_id} data-testid={`resident-item-${r.resident_id}`} disabled={busy}
                  onClick={() => create(r.resident_id)}
                  className="w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-secondary transition-colors disabled:opacity-50">
                  {r.channel === "whatsapp" ? <MessageCircle className="h-4 w-4 text-emerald-500 shrink-0" /> : <Mail className="h-4 w-4 text-[#007BFF] shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{r.nome || r.email || r.telefono}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{[r.building_name, r.email || r.telefono, r.codice_fiscale ? `C.F. ${r.codice_fiscale}` : ""].filter(Boolean).join(" · ")}</div>
                  </div>
                  {r.codice_fiscale && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">C.F. ✓</span>}
                  {r.has_portal && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground shrink-0">già attivo</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};


const ResidentAssign = ({ req, residents, selected, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // Suggerisci per email uguale a quella della richiesta
  const reqEmail = (req.email || "").trim().toLowerCase();
  const filtered = residents.filter((r) =>
    (r.nome + " " + r.email + " " + r.telefono + " " + (r.building_name || "")).toLowerCase().includes(q.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => {
    const am = (a.email || "").toLowerCase() === reqEmail ? 0 : 1;
    const bm = (b.email || "").toLowerCase() === reqEmail ? 0 : 1;
    return am - bm || (a.nome || "").localeCompare(b.nome || "");
  });

  if (selected) {
    return (
      <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid={`cred-assigned-${req.id}`}>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded-lg px-2.5 py-1.5">
          <Building2 className="h-3.5 w-3.5" />
          Collega a: {selected.nome} — {selected.building_name || "condominio n/d"}
        </span>
        <button data-testid={`cred-assign-clear-${req.id}`} onClick={onClear} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <X className="h-3 w-3" />Rimuovi abbinamento
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2">
      {!open ? (
        <button data-testid={`cred-assign-open-${req.id}`} onClick={() => setOpen(true)}
          className="text-[11px] text-[#007BFF] hover:underline inline-flex items-center gap-1 font-medium">
          <Building2 className="h-3.5 w-3.5" />Abbina a un condòmino dell'anagrafica (assegna condominio)
        </button>
      ) : (
        <div className="rounded-lg border border-border bg-secondary/40 p-2">
          <div className="relative mb-1.5">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid={`cred-assign-search-${req.id}`} value={q} onChange={(e) => setQ(e.target.value)} autoFocus
              placeholder="Cerca condòmino (nome, email, condominio)…" className="pl-8 h-8 text-xs" />
          </div>
          <div className="max-h-44 overflow-y-auto space-y-0.5">
            {sorted.length === 0 ? (
              <div className="text-center text-[11px] text-muted-foreground py-4">Nessun condòmino trovato in anagrafica.</div>
            ) : sorted.slice(0, 50).map((r) => {
              const match = (r.email || "").toLowerCase() === reqEmail && reqEmail;
              return (
                <button key={r.resident_id} data-testid={`cred-assign-item-${req.id}-${r.resident_id}`}
                  onClick={() => { onSelect(r); setOpen(false); }}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-background transition-colors">
                  <Building2 className="h-3.5 w-3.5 text-[#007BFF] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{r.nome || r.email || r.telefono}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{[r.building_name, r.email || r.telefono].filter(Boolean).join(" · ")}</div>
                  </div>
                  {match ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">email ✓</span> : null}
                </button>
              );
            })}
          </div>
          <div className="text-right mt-1">
            <button onClick={() => setOpen(false)} className="text-[11px] text-muted-foreground hover:text-foreground">Chiudi</button>
          </div>
        </div>
      )}
    </div>
  );
};
